import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { provideAbpCore, withOptions } from '@abp/ng.core';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { CUSTOM_ERROR_HANDLERS, provideAbpThemeShared } from '@abp/ng.theme.shared';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { provideCommercialUiConfig } from '@volo/abp.commercial.ng.ui/config';
import { provideAccountAdminConfig } from '@volo/abp.ng.account/admin/config';
import { provideAccountPublicConfig } from '@volo/abp.ng.account/public/config';
import { provideAuditLoggingConfig } from '@volo/abp.ng.audit-logging/config';
import { provideChatConfig } from '@volo/abp.ng.chat/config';
import { provideFileManagementConfig } from '@volo/abp.ng.file-management/config';
import { provideIdentityConfig } from '@volo/abp.ng.identity/config';
import { provideLanguageManagementConfig } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { provideOpeniddictproConfig } from '@volo/abp.ng.openiddictpro/config';
import { provideSaasConfig } from '@volo/abp.ng.saas/config';
import { provideTextTemplateManagementConfig } from '@volo/abp.ng.text-template-management/config';
import { provideLogo, withEnvironmentOptions } from '@volo/ngx-lepton-x.core';
import { provideThemeLeptonX, withThemeLeptonXOptions } from '@volosoft/abp.ng.theme.lepton-x';
import { provideSideMenuLayout } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { LPX_SETTINGS_SERVICE } from '@volosoft/ngx-lepton-x/layouts';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { AmtMenuEffects } from '@nexweb/amt/effects';
import { BmMenuEffects } from '@nexweb/bm/effects';
import { BulkDownloadEffects } from '@nexweb/bulk-download/effects';
import { CmtMenuEffects } from '@nexweb/cmt/effects';
import { EmmdMenuEffects } from '@nexweb/emmd/effects';
import { SappMtpMenuEffects } from '@nexweb/sappmtp/effects';
import { SetrepMenuEffects } from '@nexweb/setrep/effects';
import { provideIdentityMenuIcon } from '@nexweb/shared/abp/ui';
import { CustomErrorHandlerService } from '@nexweb/shared/custom-error-handler';
import { provideEffects } from '@nexweb/shared/effects';
import { provideApiJsonDateParser } from '@nexweb/shared/interceptors/api-json-date-parser';
import { provideConnectTokenRequest } from '@nexweb/shared/interceptors/connect-token-request';
import { provideKendoIconSettings } from '@nexweb/shared/kendo-icon-settings-helper';
import { provideLoading } from '@nexweb/shared/loading';
import { provideNexRefreshTokenOverrides } from '@nexweb/shared/refresh-token-services';
import { provideSystemMessaging } from '@nexweb/shared/system-messaging';
import { API_URL_TOKEN } from '@nexweb/shared/tokens/api-url';
import { INSTANCE_NAME_TOKEN } from '@nexweb/shared/tokens/instance-name';
import { KGW_CONFIG_PROVIDER } from '@nexweb/shared/ui/kgw/config';
import { MsMenuEffects } from '@nexweb/smas/effects';
import { TsamMenuEffects } from '@nexweb/tsam/effects';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { SapptpSidebarSettingsService } from './services/sapptp-sidebar-settings.service';

const LOGGERS = [NgxsLoggerPluginModule.forRoot({ disabled: false })];

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 20+ made zone-based change detection opt-in; without this the app gets
    // NoopNgZone and nothing re-renders after the initial paint.
    provideZoneChangeDetection({ eventCoalescing: true }),
    ...provideKendoIconSettings(),
    importProvidersFrom([
      BrowserModule,
      NgxsModule.forRoot([]),
      NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
      ...(environment.production ? [] : LOGGERS),
      DropDownsModule,
      BsDropdownModule,
      DialogModule,
    ]),
    // provideUserIdle(),
    provideSystemMessaging(),
    provideLoading(),
    provideApiJsonDateParser(),
    provideConnectTokenRequest(),
    provideEffects([
      AmtMenuEffects,
      BmMenuEffects,
      CmtMenuEffects,
      SetrepMenuEffects,
      MsMenuEffects,
      BulkDownloadEffects,
      TsamMenuEffects,
      SappMtpMenuEffects,
      EmmdMenuEffects,
    ]),
    provideRouter(routes),
    KGW_CONFIG_PROVIDER,
    { provide: API_URL_TOKEN, useValue: environment.apis.default.url },
    { provide: INSTANCE_NAME_TOKEN, useValue: 'mtp-web-sapptp' },
    provideAnimations(),
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
        localizations: [
          {
            culture: 'en',
            resources: [
              {
                resourceName: 'NexBase', // Use your resource name
                texts: {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  AppName: 'SAPP Market Trading Platform',
                },
              },
            ],
          },
        ],
      }),
    ),
    provideThemeLeptonX(
      withThemeLeptonXOptions({
        defaultTheme: 'light',
        themeOptions: { localStorageKey: 'theme' },
      }),
    ),
    provideSideMenuLayout(),
    {
      provide: LPX_SETTINGS_SERVICE,
      useClass: SapptpSidebarSettingsService,
    },
    provideAbpOAuth(),
    provideNexRefreshTokenOverrides(),
    provideAbpThemeShared(),
    provideAccountAdminConfig(),
    provideAccountPublicConfig(),
    provideIdentityConfig(),
    provideIdentityMenuIcon(),
    provideLanguageManagementConfig(),
    provideSaasConfig(),
    provideAuditLoggingConfig(),
    provideOpeniddictproConfig(),
    provideTextTemplateManagementConfig(),
    provideSettingManagementConfig(),
    provideFileManagementConfig(),
    provideCommercialUiConfig(),
    provideFeatureManagementConfig(),
    provideLogo(withEnvironmentOptions(environment)),
    provideChatConfig(),
    { provide: CUSTOM_ERROR_HANDLERS, useExisting: CustomErrorHandlerService, multi: true },
    APP_ROUTE_PROVIDER,
  ],
};
